import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faChevronLeft,
    faChevronRight,
    faCheck,
    faSpinner,
    faClock,
    faCheckCircle,
    faCalendar,
    faCreditCard,
    faFileContract
} from '@fortawesome/free-solid-svg-icons';
import { GPUSpec } from './gpuApi';

interface PurchaseWizardProps {
    config: GPUSpec;
    onClose: () => void;
    onSubmit: (data: PurchaseData) => Promise<void>;
}

export interface PurchaseData {
    configId: string;
    name: string;
    region: string;
    contractTerm: 1 | 3 | 6 | 12;
    sshKeyName: string;
}

interface ContractTerm {
    months: 1 | 3 | 6 | 12;
    label: string;
    discount: number; // percentage
    popular?: boolean;
}

const CONTRACT_TERMS: ContractTerm[] = [
    { months: 1, label: '1 Month', discount: 0 },
    { months: 3, label: '3 Months', discount: 5, popular: true },
    { months: 6, label: '6 Months', discount: 10 },
    { months: 12, label: '12 Months', discount: 15 }
];

interface AvailabilityInfo {
    status: 'instant' | 'lead_time';
    leadTimeDays?: number;
    stockLevel: 'high' | 'medium' | 'low';
    message: string;
}

const PurchaseWizard: React.FC<PurchaseWizardProps> = ({ config, onClose, onSubmit }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form data
    const [contractTerm, setContractTerm] = useState<1 | 3 | 6 | 12>(3);
    const [instanceName, setInstanceName] = useState(`${config.gpuType}-instance-${Date.now()}`);
    const [selectedRegion, setSelectedRegion] = useState(config.regions[0] || 'US-East');
    const [sshKeyName, setSshKeyName] = useState('default-key');

    // Calculate pricing based on term
    const getDiscountedPrice = () => {
        const term = CONTRACT_TERMS.find(t => t.months === contractTerm);
        const discount = term?.discount || 0;
        const monthlyPrice = config.pricing.monthly;
        const discountedMonthly = monthlyPrice * (1 - discount / 100);
        const totalPrice = discountedMonthly * contractTerm;

        return {
            originalMonthly: monthlyPrice,
            discountedMonthly,
            discount,
            totalPrice,
            savings: (monthlyPrice - discountedMonthly) * contractTerm
        };
    };

    // Simulate availability check based on GPU type
    const getAvailability = (): AvailabilityInfo => {
        // H100 and H200 have lead time, others are instant
        if (config.gpuType === 'H100' || config.gpuType === 'H200') {
            return {
                status: 'lead_time',
                leadTimeDays: 7,
                stockLevel: 'low',
                message: 'High-demand GPU. Provisioning typically takes 5-7 business days.'
            };
        } else if (config.gpuType === 'A100') {
            return {
                status: 'lead_time',
                leadTimeDays: 3,
                stockLevel: 'medium',
                message: 'Usually available within 2-3 business days.'
            };
        }
        return {
            status: 'instant',
            stockLevel: 'high',
            message: 'Instance will be provisioned immediately after payment.'
        };
    };

    const pricing = getDiscountedPrice();
    const availability = getAvailability();

    const steps = [
        { number: 1, title: 'Configuration', icon: faCalendar },
        { number: 2, title: 'Instance Details', icon: faCheckCircle },
        { number: 3, title: 'Review & Payment', icon: faCreditCard },
        { number: 4, title: 'Confirmation', icon: faFileContract }
    ];

    const canProceed = () => {
        switch (currentStep) {
            case 1:
                return contractTerm > 0;
            case 2:
                return instanceName.trim().length > 0 && selectedRegion.length > 0;
            case 3:
                return true;
            case 4:
                return true;
            default:
                return false;
        }
    };

    const handleNext = () => {
        if (canProceed() && currentStep < 4) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async () => {
        if (!canProceed()) return;

        setIsSubmitting(true);
        try {
            await onSubmit({
                configId: config.id,
                name: instanceName,
                region: selectedRegion,
                contractTerm,
                sshKeyName
            });
        } catch (error) {
            console.error('Purchase failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto font-mono">
                {/* Header */}
                <div className="sticky top-0 bg-blue-600 px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
                    <div className="text-white">
                        <h2 className="text-lg font-bold">Launch GPU Instance</h2>
                        <p className="text-sm text-blue-100">{config.name}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-blue-200"
                        disabled={isSubmitting}
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-xl" />
                    </button>
                </div>

                {/* Progress Steps */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        {steps.map((step, index) => (
                            <React.Fragment key={step.number}>
                                <div className="flex flex-col items-center flex-1">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                                            currentStep > step.number
                                                ? 'bg-green-600 text-white'
                                                : currentStep === step.number
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-300 text-gray-600'
                                        }`}
                                    >
                                        {currentStep > step.number ? (
                                            <FontAwesomeIcon icon={faCheck} />
                                        ) : (
                                            <FontAwesomeIcon icon={step.icon} />
                                        )}
                                    </div>
                                    <div className={`text-xs mt-2 text-center font-medium ${
                                        currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'
                                    }`}>
                                        {step.title}
                                    </div>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`flex-1 h-1 mx-2 ${
                                        currentStep > step.number ? 'bg-green-600' : 'bg-gray-300'
                                    }`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Step 1: Configuration & Term */}
                    {currentStep === 1 && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-base font-bold mb-4">Select Contract Term</h3>
                                <div className="grid grid-cols-2 gap-3">
                                    {CONTRACT_TERMS.map((term) => (
                                        <button
                                            key={term.months}
                                            onClick={() => setContractTerm(term.months)}
                                            className={`relative p-4 border-2 rounded-lg text-left transition-all ${
                                                contractTerm === term.months
                                                    ? 'border-blue-600 bg-blue-50'
                                                    : 'border-gray-300 hover:border-blue-300'
                                            }`}
                                        >
                                            {term.popular && (
                                                <span className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded font-bold">
                                                    POPULAR
                                                </span>
                                            )}
                                            <div className="font-bold text-sm mb-1">{term.label}</div>
                                            {term.discount > 0 && (
                                                <div className="text-xs text-green-600 font-bold mb-2">
                                                    Save {term.discount}%
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-600">
                                                ${(config.pricing.monthly * (1 - term.discount / 100)).toFixed(2)}/month
                                            </div>
                                            <div className="text-sm font-bold text-blue-700 mt-2">
                                                ${(config.pricing.monthly * (1 - term.discount / 100) * term.months).toFixed(2)} total
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Pricing Summary */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="text-sm font-bold mb-3">Pricing Summary</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">GPU Configuration:</span>
                                        <span className="font-bold">{config.gpuCount}x NVIDIA {config.gpuType}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Original Price:</span>
                                        <span className="line-through text-gray-500">${pricing.originalMonthly}/month</span>
                                    </div>
                                    {pricing.discount > 0 && (
                                        <>
                                            <div className="flex justify-between text-green-600">
                                                <span className="font-medium">Discount ({pricing.discount}%):</span>
                                                <span className="font-bold">-${(pricing.originalMonthly - pricing.discountedMonthly).toFixed(2)}/month</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Discounted Price:</span>
                                                <span className="font-bold text-blue-700">${pricing.discountedMonthly.toFixed(2)}/month</span>
                                            </div>
                                        </>
                                    )}
                                    <div className="border-t border-blue-300 pt-2 mt-2">
                                        <div className="flex justify-between items-center">
                                            <span className="font-bold">Total for {contractTerm} month{contractTerm > 1 ? 's' : ''}:</span>
                                            <span className="text-xl font-bold text-blue-700">${pricing.totalPrice.toFixed(2)}</span>
                                        </div>
                                        {pricing.savings > 0 && (
                                            <div className="text-xs text-green-600 text-right mt-1">
                                                You save ${pricing.savings.toFixed(2)}!
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* GPU Specs */}
                            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                                <h4 className="text-sm font-bold mb-3">Configuration Details</h4>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                    <div><span className="text-gray-600">CPU:</span> <span className="font-bold">{config.specifications.cpu}</span></div>
                                    <div><span className="text-gray-600">RAM:</span> <span className="font-bold">{config.specifications.ram}</span></div>
                                    <div><span className="text-gray-600">VRAM:</span> <span className="font-bold">{config.specifications.vram}</span></div>
                                    <div><span className="text-gray-600">Storage:</span> <span className="font-bold">{config.specifications.storage}</span></div>
                                    <div><span className="text-gray-600">CUDA Cores:</span> <span className="font-bold">{config.specifications.cudaCores.toLocaleString()}</span></div>
                                    <div><span className="text-gray-600">Tensor Cores:</span> <span className="font-bold">{config.specifications.tensorCores}</span></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Instance Details */}
                    {currentStep === 2 && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-base font-bold mb-4">Configure Instance</h3>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-bold mb-2">Instance Name *</label>
                                        <input
                                            type="text"
                                            value={instanceName}
                                            onChange={(e) => setInstanceName(e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="my-gpu-instance"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Choose a descriptive name for your instance</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold mb-2">Region *</label>
                                        <select
                                            value={selectedRegion}
                                            onChange={(e) => setSelectedRegion(e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {config.regions.map(region => (
                                                <option key={region} value={region}>{region}</option>
                                            ))}
                                        </select>
                                        <p className="text-xs text-gray-500 mt-1">Select the geographical region closest to your workload</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold mb-2">SSH Key Name</label>
                                        <input
                                            type="text"
                                            value={sshKeyName}
                                            onChange={(e) => setSshKeyName(e.target.value)}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="default-key"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">SSH key for secure access (optional)</p>
                                    </div>
                                </div>
                            </div>

                            {/* Availability Info */}
                            <div className={`border-2 rounded-lg p-4 ${
                                availability.status === 'instant'
                                    ? 'bg-green-50 border-green-300'
                                    : 'bg-yellow-50 border-yellow-300'
                            }`}>
                                <div className="flex items-start gap-3">
                                    <FontAwesomeIcon
                                        icon={availability.status === 'instant' ? faCheckCircle : faClock}
                                        className={`text-2xl mt-1 ${
                                            availability.status === 'instant' ? 'text-green-600' : 'text-yellow-600'
                                        }`}
                                    />
                                    <div className="flex-1">
                                        <h4 className="text-sm font-bold mb-1">
                                            {availability.status === 'instant' ? 'Instant Availability' : 'Lead Time Required'}
                                        </h4>
                                        <p className="text-sm text-gray-700 mb-2">{availability.message}</p>
                                        {availability.status === 'lead_time' && availability.leadTimeDays && (
                                            <div className="text-sm">
                                                <span className="font-bold text-yellow-700">
                                                    Estimated Provisioning: {availability.leadTimeDays} business days
                                                </span>
                                            </div>
                                        )}
                                        <div className="mt-2">
                                            <span className="text-xs font-medium">Current Stock Level: </span>
                                            <span className={`text-xs font-bold ${
                                                availability.stockLevel === 'high' ? 'text-green-600' :
                                                availability.stockLevel === 'medium' ? 'text-yellow-600' :
                                                'text-red-600'
                                            }`}>
                                                {availability.stockLevel.toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Review & Payment */}
                    {currentStep === 3 && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-base font-bold mb-4">Review Your Order</h3>

                                {/* Order Summary */}
                                <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-4">
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <div className="text-gray-600 text-xs mb-1">Configuration</div>
                                            <div className="font-bold">{config.name}</div>
                                        </div>
                                        <div>
                                            <div className="text-gray-600 text-xs mb-1">Contract Term</div>
                                            <div className="font-bold">{contractTerm} Month{contractTerm > 1 ? 's' : ''}</div>
                                        </div>
                                        <div>
                                            <div className="text-gray-600 text-xs mb-1">Instance Name</div>
                                            <div className="font-bold">{instanceName}</div>
                                        </div>
                                        <div>
                                            <div className="text-gray-600 text-xs mb-1">Region</div>
                                            <div className="font-bold">{selectedRegion}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Billing Summary */}
                                <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 mb-4">
                                    <h4 className="text-sm font-bold mb-3">Billing Summary</h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Subtotal ({contractTerm} months):</span>
                                            <span className="font-bold">${(pricing.originalMonthly * contractTerm).toFixed(2)}</span>
                                        </div>
                                        {pricing.discount > 0 && (
                                            <div className="flex justify-between text-green-600">
                                                <span className="font-medium">Volume Discount ({pricing.discount}%):</span>
                                                <span className="font-bold">-${pricing.savings.toFixed(2)}</span>
                                            </div>
                                        )}
                                        <div className="border-t border-blue-300 pt-2 flex justify-between items-center">
                                            <span className="font-bold text-base">Total Due Today:</span>
                                            <span className="text-2xl font-bold text-blue-700">${pricing.totalPrice.toFixed(2)}</span>
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            Billed as ${pricing.discountedMonthly.toFixed(2)}/month for {contractTerm} month{contractTerm > 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

                    {/* Step 4: Confirmation */}
                    {currentStep === 4 && !isSubmitting && (
                        <div className="space-y-4">
                            <h3 className="text-base font-bold mb-4">Order Confirmation</h3>

                            {/* Instance Configuration */}
                            <div className="border border-gray-300 rounded p-3">
                                <h4 className="text-sm font-bold mb-3">Instance Configuration</h4>
                                <table className="w-full text-xs">
                                    <tbody>
                                        <tr className="border-b border-gray-200">
                                            <td className="py-2 pr-4 text-gray-600">Instance Name:</td>
                                            <td className="py-2 font-bold">{instanceName}</td>
                                        </tr>
                                        <tr className="border-b border-gray-200">
                                            <td className="py-2 pr-4 text-gray-600">GPU Type:</td>
                                            <td className="py-2 font-bold">{config.gpuCount}x NVIDIA {config.gpuType}</td>
                                        </tr>
                                        <tr className="border-b border-gray-200">
                                            <td className="py-2 pr-4 text-gray-600">Region:</td>
                                            <td className="py-2 font-bold">{selectedRegion}</td>
                                        </tr>
                                        <tr className="border-b border-gray-200">
                                            <td className="py-2 pr-4 text-gray-600">CPU:</td>
                                            <td className="py-2 font-bold">{config.specifications.cpu}</td>
                                        </tr>
                                        <tr className="border-b border-gray-200">
                                            <td className="py-2 pr-4 text-gray-600">RAM:</td>
                                            <td className="py-2 font-bold">{config.specifications.ram}</td>
                                        </tr>
                                        <tr className="border-b border-gray-200">
                                            <td className="py-2 pr-4 text-gray-600">Storage:</td>
                                            <td className="py-2 font-bold">{config.specifications.storage}</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 pr-4 text-gray-600">SSH Key:</td>
                                            <td className="py-2 font-bold">{sshKeyName}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Billing Summary */}
                            <div className="border border-gray-300 rounded p-3">
                                <h4 className="text-sm font-bold mb-3">Billing Summary</h4>
                                <table className="w-full text-xs">
                                    <tbody>
                                        <tr className="border-b border-gray-200">
                                            <td className="py-2 pr-4 text-gray-600">Contract Term:</td>
                                            <td className="py-2 font-bold">{contractTerm} Month{contractTerm > 1 ? 's' : ''}</td>
                                        </tr>
                                        <tr className="border-b border-gray-200">
                                            <td className="py-2 pr-4 text-gray-600">Monthly Rate:</td>
                                            <td className="py-2 font-bold">${pricing.discountedMonthly.toFixed(2)}/month</td>
                                        </tr>
                                        {pricing.discount > 0 && (
                                            <tr className="border-b border-gray-200">
                                                <td className="py-2 pr-4 text-gray-600">Discount:</td>
                                                <td className="py-2 font-bold text-green-600">{pricing.discount}% ({contractTerm}-month term)</td>
                                            </tr>
                                        )}
                                        <tr className="bg-blue-50">
                                            <td className="py-2 pr-4 font-bold text-gray-900">Total Amount Due:</td>
                                            <td className="py-2 text-xl font-bold text-blue-700">${pricing.totalPrice.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Provisioning Info */}
                            <div className={`border rounded p-3 ${
                                availability.status === 'instant'
                                    ? 'bg-green-50 border-green-300'
                                    : 'bg-yellow-50 border-yellow-300'
                            }`}>
                                <h4 className="text-sm font-bold mb-2 flex items-center gap-2">
                                    <FontAwesomeIcon icon={availability.status === 'instant' ? faCheckCircle : faClock} />
                                    Provisioning Timeline
                                </h4>
                                <p className="text-xs">
                                    {availability.message}
                                    {availability.status === 'lead_time' && availability.leadTimeDays && (
                                        <span className="block mt-1 font-bold">
                                            Estimated time: {availability.leadTimeDays} business days
                                        </span>
                                    )}
                                </p>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={handleBack}
                                    className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 font-medium"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
                                    Back
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-bold"
                                >
                                    <FontAwesomeIcon icon={faCreditCard} className="mr-2" />
                                    Proceed to Payment
                                </button>
                            </div>

                            <p className="text-xs text-gray-500 text-center">
                                You'll be redirected to Stripe to complete payment. Provisioning begins once payment succeeds.
                            </p>
                        </div>
                    )}

                    {isSubmitting && (
                        <div className="border border-gray-300 rounded p-6 text-center">
                            <FontAwesomeIcon icon={faSpinner} spin className="text-blue-600 text-3xl mb-3" />
                            <h3 className="text-base font-bold text-gray-900 mb-2">Redirecting to Stripe…</h3>
                            <p className="text-sm text-gray-600">Please wait while we prepare your checkout session.</p>
                        </div>
                    )}
                </div>

                {/* Footer with Navigation */}
                {currentStep < 4 && !isSubmitting && (
                    <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center rounded-b-lg">
                        <button
                            onClick={handleBack}
                            disabled={currentStep === 1}
                            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
                            Back
                        </button>

                        <div className="text-sm text-gray-600">
                            Step {currentStep} of {steps.length}
                        </div>

                        <button
                            onClick={handleNext}
                            disabled={!canProceed()}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                        >
                            {currentStep === 3 ? 'Review Order' : 'Continue'}
                            <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PurchaseWizard;
